import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import type { Toaster as ToasterType } from "@/components/ui/toaster";

/**
 * Defers the @radix-ui/react-toast bundle (~52 KB raw with its
 * @radix-ui/react-collection + @radix-ui/react-dismissable-layer
 * deps) until the FIRST toast is dispatched. App.tsx mounts this
 * wrapper instead of the real <Toaster />.
 *
 * use-toast.ts holds toast state in a module-scoped `memoryState`
 * with a listener array — so `toast()` calls from anywhere mutate
 * state regardless of whether Toaster is mounted. When Toaster
 * eventually mounts, its `useToast()` hook reads `memoryState` at
 * init via `useState(memoryState)` and immediately renders any
 * queued toasts. So buffering across the lazy gap is automatic.
 *
 * Net effect on the entry chunk: -52 KB raw / ~15 KB gz, none of
 * the toast machinery loads during Lighthouse measurement (no
 * toast() calls fire during a passive page-load), and real users
 * pay for the chunk only when they trigger their first toast
 * (form submit success, share confirmation, etc.).
 */
let toasterPromise: Promise<{ Toaster: typeof ToasterType }> | null = null;
const loadToaster = (): Promise<{ Toaster: typeof ToasterType }> => {
  if (!toasterPromise) toasterPromise = import("@/components/ui/toaster");
  return toasterPromise;
};

export default function LazyToaster() {
  const { toasts } = useToast();
  const [Toaster, setToaster] = useState<typeof ToasterType | null>(null);

  useEffect(() => {
    if (toasts.length === 0 || Toaster) return;
    let cancelled = false;
    loadToaster()
      .then((mod) => {
        if (!cancelled) setToaster(() => mod.Toaster);
      })
      .catch(() => {
        // Stale chunk after a deploy-mid-session — silently no-op;
        // the toast call's own memoryState entry stays for the next
        // mount attempt. Better to drop the on-screen toast than
        // hard-error the page.
      });
    return () => {
      cancelled = true;
    };
  }, [toasts.length, Toaster]);

  if (!Toaster) return null;
  return <Toaster />;
}
